import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  Clock,
  Calendar,
  FileText,
  Loader2,
  Save,
  RotateCcw,
  Calculator,
  Pill,
  Timer,
  Sun,
  Moon,
  X,
  Plus,
  AlertCircle,
  CheckCircle2,
  ArrowRightLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SCHEDULING_RULES = {
  min_lead_time_hours: 24,
  service_window_hours: 4,
  preferred_shift: 'ANY',
  weekend_policy: 'next_business_day',
  respect_holidays: true,
  requires_note: false,
  auto_note: '',
  requires_empalme: false,
  empalme_shift: 'AM',
  empalme_note: '',
  is_active: true,
  // Corte por hora (shift_based) -- ver docs/client_request_rules/PLAN-IMPLEMENTACION-REGLAS.md §3.4/§4.
  // Hasta ahora esta configuración solo se podía cargar por script (Fase 2 la expone en esta UI).
  scheduling_type: 'standard',
  cutoff_time: '12:00',
  before_cutoff_shift: 'pm',
  before_cutoff_next_day: false,
  after_cutoff_shift: 'am',
  after_cutoff_next_day: true,
  after_cutoff_add_extra_day: false,
  am_shift: { start: '08:00', end: '12:00' },
  pm_shift: { start: '14:00', end: '18:00' }
};

const DEFAULT_MEDICATION_RULES = {
  enabled: false,
  frequency_based: true,
  equalization_hours: [6, 14, 20],
  continuous_infusion_offset_hours: 8
};

export const SchedulingRulesDialog = ({ open, onOpenChange, service, onSave }) => {
  const { t } = useTranslation(['scheduling', 'common']);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  const [schedulingRules, setSchedulingRules] = useState(DEFAULT_SCHEDULING_RULES);
  const [medicationRules, setMedicationRules] = useState(DEFAULT_MEDICATION_RULES);
  const [newEqHour, setNewEqHour] = useState('');
  
  // Preview state
  const [previewDateTime, setPreviewDateTime] = useState('');
  const [previewFrequency, setPreviewFrequency] = useState('none');
  const [previewResult, setPreviewResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    if (open && service?.id) {
      fetchConfig();
      // Set default preview datetime to now
      const now = new Date();
      setPreviewDateTime(now.toISOString().slice(0, 16));
    }
  }, [open, service?.id]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/scheduling/services/${service.id}/config`
      );
      
      const config = response.data;
      if (config.scheduling_rules) {
        setSchedulingRules({ ...DEFAULT_SCHEDULING_RULES, ...config.scheduling_rules });
      }
      if (config.medication_rules) {
        setMedicationRules({ ...DEFAULT_MEDICATION_RULES, ...config.medication_rules });
      }
    } catch (error) {
      console.error('Error fetching scheduling config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/scheduling/services/${service.id}/config`,
        {
          scheduling_rules: schedulingRules,
          medication_rules: medicationRules.enabled ? medicationRules : null
        }
      );
      
      toast.success(t('messages.saveSuccess'));
      onSave?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(t('messages.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      await axios.delete(`${API_URL}/api/scheduling/services/${service.id}/config`);
      setSchedulingRules(DEFAULT_SCHEDULING_RULES);
      setMedicationRules(DEFAULT_MEDICATION_RULES);
      toast.success(t('messages.resetSuccess'));
    } catch (error) {
      toast.error(t('messages.resetError'));
    }
  };

  const handlePreview = async () => {
    if (!previewDateTime) return;
    
    setCalculating(true);
    try {
      // First save the current config temporarily
      await axios.put(
        `${API_URL}/api/scheduling/services/${service.id}/config`,
        {
          scheduling_rules: schedulingRules,
          medication_rules: medicationRules.enabled ? medicationRules : null
        }
      );
      
      const response = await axios.post(
        `${API_URL}/api/scheduling/calculate/preview`,
        {
          service_id: service.id,
          order_received_at: new Date(previewDateTime).toISOString(),
          medication_frequency: previewFrequency === 'none' ? null : previewFrequency
        }
      );
      
      setPreviewResult(response.data);
    } catch (error) {
      toast.error(t('messages.calculateError'));
    } finally {
      setCalculating(false);
    }
  };

  const addEqualizationHour = () => {
    const hour = parseInt(newEqHour);
    if (isNaN(hour) || hour < 0 || hour > 23) return;
    if (medicationRules.equalization_hours.includes(hour)) return;
    
    setMedicationRules(prev => ({
      ...prev,
      equalization_hours: [...prev.equalization_hours, hour].sort((a, b) => a - b)
    }));
    setNewEqHour('');
  };

  const removeEqualizationHour = (hour) => {
    setMedicationRules(prev => ({
      ...prev,
      equalization_hours: prev.equalization_hours.filter(h => h !== hour)
    }));
  };

  const formatHour = (hour) => {
    const h = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${h}:00 ${ampm}`;
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl">
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#E63946]" />
            {t('title')}: {service?.name}
          </DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic" className="gap-2">
              <Timer className="h-4 w-4" />
              {t('tabs.basic')}
            </TabsTrigger>
            <TabsTrigger value="medication" className="gap-2">
              <Pill className="h-4 w-4" />
              {t('tabs.medication')}
            </TabsTrigger>
            <TabsTrigger value="preview" className="gap-2">
              <Calculator className="h-4 w-4" />
              {t('tabs.preview')}
            </TabsTrigger>
          </TabsList>

          {/* Basic Rules Tab */}
          <TabsContent value="basic" className="space-y-6 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('basicRules.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('basicRules.minLeadTime')}</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        value={schedulingRules.min_lead_time_hours}
                        onChange={(e) => setSchedulingRules(prev => ({
                          ...prev,
                          min_lead_time_hours: parseInt(e.target.value) || 0
                        }))}
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground">horas</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{t('basicRules.minLeadTimeHelp')}</p>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('basicRules.serviceWindow')}</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        value={schedulingRules.service_window_hours}
                        onChange={(e) => setSchedulingRules(prev => ({
                          ...prev,
                          service_window_hours: parseInt(e.target.value) || 1
                        }))}
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground">horas</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{t('basicRules.serviceWindowHelp')}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('basicRules.preferredShift')}</Label>
                    <Select
                      value={schedulingRules.preferred_shift}
                      onValueChange={(value) => setSchedulingRules(prev => ({
                        ...prev,
                        preferred_shift: value
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AM">
                          <span className="flex items-center gap-2">
                            <Sun className="h-4 w-4" />
                            {t('basicRules.shifts.AM')}
                          </span>
                        </SelectItem>
                        <SelectItem value="PM">
                          <span className="flex items-center gap-2">
                            <Moon className="h-4 w-4" />
                            {t('basicRules.shifts.PM')}
                          </span>
                        </SelectItem>
                        <SelectItem value="ANY">{t('basicRules.shifts.ANY')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('basicRules.weekendPolicy')}</Label>
                    <Select
                      value={schedulingRules.weekend_policy}
                      onValueChange={(value) => setSchedulingRules(prev => ({
                        ...prev,
                        weekend_policy: value
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="next_business_day">
                          {t('basicRules.weekendPolicies.next_business_day')}
                        </SelectItem>
                        <SelectItem value="saturday_only">
                          {t('basicRules.weekendPolicies.saturday_only')}
                        </SelectItem>
                        <SelectItem value="allow_weekends">
                          {t('basicRules.weekendPolicies.allow_weekends')}
                        </SelectItem>
                        <SelectItem value="none">
                          {t('basicRules.weekendPolicies.none')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <Label>{t('basicRules.respectHolidays')}</Label>
                    <p className="text-xs text-muted-foreground">{t('basicRules.respectHolidaysHelp')}</p>
                  </div>
                  <Switch
                    checked={schedulingRules.respect_holidays}
                    onCheckedChange={(checked) => setSchedulingRules(prev => ({
                      ...prev,
                      respect_holidays: checked
                    }))}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Cutoff Rules Section (shift_based) */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4" />
                  {t('cutoffRules.title')}
                </CardTitle>
                <CardDescription>{t('cutoffRules.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('cutoffRules.schedulingType')}</Label>
                  <Select
                    value={schedulingRules.scheduling_type}
                    onValueChange={(value) => setSchedulingRules(prev => ({
                      ...prev,
                      scheduling_type: value
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">{t('cutoffRules.schedulingTypes.standard')}</SelectItem>
                      <SelectItem value="shift_based">{t('cutoffRules.schedulingTypes.shift_based')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {schedulingRules.scheduling_type === 'shift_based' && (
                  <>
                    <div className="space-y-2">
                      <Label>{t('cutoffRules.cutoffTime')}</Label>
                      <Input
                        type="time"
                        value={schedulingRules.cutoff_time}
                        onChange={(e) => setSchedulingRules(prev => ({
                          ...prev,
                          cutoff_time: e.target.value
                        }))}
                        className="w-32"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Before cutoff */}
                      <div className="space-y-3 p-3 rounded-lg border">
                        <Label className="font-medium">{t('cutoffRules.before')}</Label>
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">{t('cutoffRules.targetShift')}</Label>
                          <Select
                            value={schedulingRules.before_cutoff_shift}
                            onValueChange={(value) => setSchedulingRules(prev => ({
                              ...prev,
                              before_cutoff_shift: value
                            }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="am">{t('basicRules.shifts.AM')}</SelectItem>
                              <SelectItem value="pm">{t('basicRules.shifts.PM')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">{t('cutoffRules.nextDay')}</Label>
                          <Switch
                            checked={schedulingRules.before_cutoff_next_day}
                            onCheckedChange={(checked) => setSchedulingRules(prev => ({
                              ...prev,
                              before_cutoff_next_day: checked
                            }))}
                          />
                        </div>
                      </div>

                      {/* After cutoff */}
                      <div className="space-y-3 p-3 rounded-lg border">
                        <Label className="font-medium">{t('cutoffRules.after')}</Label>
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">{t('cutoffRules.targetShift')}</Label>
                          <Select
                            value={schedulingRules.after_cutoff_shift}
                            onValueChange={(value) => setSchedulingRules(prev => ({
                              ...prev,
                              after_cutoff_shift: value
                            }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="am">{t('basicRules.shifts.AM')}</SelectItem>
                              <SelectItem value="pm">{t('basicRules.shifts.PM')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">{t('cutoffRules.nextDay')}</Label>
                          <Switch
                            checked={schedulingRules.after_cutoff_next_day}
                            onCheckedChange={(checked) => setSchedulingRules(prev => ({
                              ...prev,
                              after_cutoff_next_day: checked
                            }))}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">{t('cutoffRules.addExtraDay')}</Label>
                          <Switch
                            checked={schedulingRules.after_cutoff_add_extra_day}
                            onCheckedChange={(checked) => setSchedulingRules(prev => ({
                              ...prev,
                              after_cutoff_add_extra_day: checked
                            }))}
                          />
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Sun className="h-4 w-4" />
                          {t('cutoffRules.amShiftRange')}
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={schedulingRules.am_shift.start}
                            onChange={(e) => setSchedulingRules(prev => ({
                              ...prev,
                              am_shift: { ...prev.am_shift, start: e.target.value }
                            }))}
                          />
                          <span className="text-sm text-muted-foreground">{t('cutoffRules.to')}</span>
                          <Input
                            type="time"
                            value={schedulingRules.am_shift.end}
                            onChange={(e) => setSchedulingRules(prev => ({
                              ...prev,
                              am_shift: { ...prev.am_shift, end: e.target.value }
                            }))}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Moon className="h-4 w-4" />
                          {t('cutoffRules.pmShiftRange')}
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={schedulingRules.pm_shift.start}
                            onChange={(e) => setSchedulingRules(prev => ({
                              ...prev,
                              pm_shift: { ...prev.pm_shift, start: e.target.value }
                            }))}
                          />
                          <span className="text-sm text-muted-foreground">{t('cutoffRules.to')}</span>
                          <Input
                            type="time"
                            value={schedulingRules.pm_shift.end}
                            onChange={(e) => setSchedulingRules(prev => ({
                              ...prev,
                              pm_shift: { ...prev.pm_shift, end: e.target.value }
                            }))}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Notes Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('notes.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>{t('notes.requiresNote')}</Label>
                  <Switch
                    checked={schedulingRules.requires_note}
                    onCheckedChange={(checked) => setSchedulingRules(prev => ({
                      ...prev,
                      requires_note: checked
                    }))}
                  />
                </div>
                
                {schedulingRules.requires_note && (
                  <div className="space-y-2">
                    <Label>{t('notes.autoNote')}</Label>
                    <Textarea
                      value={schedulingRules.auto_note || ''}
                      onChange={(e) => setSchedulingRules(prev => ({
                        ...prev,
                        auto_note: e.target.value
                      }))}
                      placeholder={t('notes.autoNotePlaceholder')}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Empalme Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('empalme.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <Label>{t('empalme.requiresEmpalme')}</Label>
                    <p className="text-xs text-muted-foreground">{t('empalme.requiresEmpalmeHelp')}</p>
                  </div>
                  <Switch
                    checked={schedulingRules.requires_empalme}
                    onCheckedChange={(checked) => setSchedulingRules(prev => ({
                      ...prev,
                      requires_empalme: checked
                    }))}
                  />
                </div>
                
                {schedulingRules.requires_empalme && (
                  <>
                    <div className="space-y-2">
                      <Label>{t('empalme.empalmeShift')}</Label>
                      <Select
                        value={schedulingRules.empalme_shift}
                        onValueChange={(value) => setSchedulingRules(prev => ({
                          ...prev,
                          empalme_shift: value
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AM">{t('basicRules.shifts.AM')}</SelectItem>
                          <SelectItem value="PM">{t('basicRules.shifts.PM')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>{t('empalme.empalmeNote')}</Label>
                      <Textarea
                        value={schedulingRules.empalme_note || ''}
                        onChange={(e) => setSchedulingRules(prev => ({
                          ...prev,
                          empalme_note: e.target.value
                        }))}
                        placeholder={t('empalme.empalmeNotePlaceholder')}
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Medication Rules Tab */}
          <TabsContent value="medication" className="space-y-6 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('medication.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <Label>{t('medication.enabled')}</Label>
                    <p className="text-xs text-muted-foreground">{t('medication.enabledHelp')}</p>
                  </div>
                  <Switch
                    checked={medicationRules.enabled}
                    onCheckedChange={(checked) => setMedicationRules(prev => ({
                      ...prev,
                      enabled: checked
                    }))}
                  />
                </div>
                
                {medicationRules.enabled && (
                  <>
                    <Separator />
                    
                    <div className="space-y-2">
                      <Label>{t('medication.equalizationHours')}</Label>
                      <p className="text-xs text-muted-foreground">{t('medication.equalizationHoursHelp')}</p>
                      
                      <div className="flex flex-wrap gap-2 mt-2">
                        {medicationRules.equalization_hours.map(hour => (
                          <Badge
                            key={hour}
                            variant="secondary"
                            className="gap-1 cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => removeEqualizationHour(hour)}
                          >
                            {formatHour(hour)}
                            <X className="h-3 w-3" />
                          </Badge>
                        ))}
                      </div>
                      
                      <div className="flex gap-2 mt-2">
                        <Input
                          type="number"
                          min={0}
                          max={23}
                          value={newEqHour}
                          onChange={(e) => setNewEqHour(e.target.value)}
                          placeholder="0-23"
                          className="w-24"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addEqualizationHour}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {t('medication.addHour')}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>{t('medication.continuousOffset')}</Label>
                      <p className="text-xs text-muted-foreground">{t('medication.continuousOffsetHelp')}</p>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={1}
                          value={medicationRules.continuous_infusion_offset_hours}
                          onChange={(e) => setMedicationRules(prev => ({
                            ...prev,
                            continuous_infusion_offset_hours: parseInt(e.target.value) || 8
                          }))}
                          className="w-24"
                        />
                        <span className="text-sm text-muted-foreground">horas</span>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="space-y-2">
                      <Label>Frecuencias soportadas</Label>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="p-2 bg-muted rounded">
                          <strong>{t('medication.frequencies.24h')}:</strong> Día siguiente (excepto 00:01-3:00 AM)
                        </div>
                        <div className="p-2 bg-muted rounded">
                          <strong>{t('medication.frequencies.12h')}:</strong> Alternar jornadas AM/PM
                        </div>
                        <div className="p-2 bg-muted rounded">
                          <strong>{t('medication.frequencies.8h')}:</strong> Igualar a horarios configurados
                        </div>
                        <div className="p-2 bg-muted rounded">
                          <strong>{t('medication.frequencies.6h')}:</strong> Igualar a horarios configurados
                        </div>
                        <div className="p-2 bg-muted rounded col-span-2">
                          <strong>{t('medication.frequencies.4h')} / {t('medication.frequencies.continuous')}:</strong> +{medicationRules.continuous_infusion_offset_hours}h desde llegada
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preview Tab */}
          <TabsContent value="preview" className="space-y-6 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('preview.title')}</CardTitle>
                <CardDescription>
                  Prueba el cálculo de programación con diferentes fechas y frecuencias
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('preview.testDateTime')}</Label>
                    <Input
                      type="datetime-local"
                      value={previewDateTime}
                      onChange={(e) => setPreviewDateTime(e.target.value)}
                    />
                  </div>
                  
                  {medicationRules.enabled && (
                    <div className="space-y-2">
                      <Label>{t('preview.testFrequency')}</Label>
                      <Select
                        value={previewFrequency}
                        onValueChange={setPreviewFrequency}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sin frecuencia" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin frecuencia</SelectItem>
                          <SelectItem value="4h">{t('medication.frequencies.4h')}</SelectItem>
                          <SelectItem value="6h">{t('medication.frequencies.6h')}</SelectItem>
                          <SelectItem value="8h">{t('medication.frequencies.8h')}</SelectItem>
                          <SelectItem value="12h">{t('medication.frequencies.12h')}</SelectItem>
                          <SelectItem value="24h">{t('medication.frequencies.24h')}</SelectItem>
                          <SelectItem value="continuous">{t('medication.frequencies.continuous')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                
                <Button onClick={handlePreview} disabled={calculating || !previewDateTime}>
                  {calculating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Calculator className="h-4 w-4 mr-2" />
                  )}
                  {t('preview.calculate')}
                </Button>
                
                {previewResult && (
                  <div className="p-4 bg-muted rounded-lg space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span className="font-medium">Resultado del cálculo</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">{t('preview.result.scheduledDate')}:</span>
                        <p className="font-medium">{new Date(previewResult.scheduled_date).toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">{t('preview.result.scheduledTime')}:</span>
                        <p className="font-medium">{previewResult.scheduled_time_start} - {previewResult.scheduled_time_end}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">{t('preview.result.shift')}:</span>
                        <Badge variant={previewResult.shift === 'AM' ? 'default' : 'secondary'}>
                          {previewResult.shift === 'AM' ? '🌅 Mañana' : '🌙 Tarde'}
                        </Badge>
                      </div>
                      {previewResult.requires_empalme && (
                        <div>
                          <span className="text-muted-foreground">{t('preview.result.empalme')}:</span>
                          <p className="font-medium">{previewResult.empalme_date} - {previewResult.empalme_time}</p>
                        </div>
                      )}
                    </div>
                    
                    {previewResult.auto_notes?.length > 0 && (
                      <div>
                        <span className="text-muted-foreground text-sm">{t('preview.result.notes')}:</span>
                        <ul className="list-disc list-inside text-sm">
                          {previewResult.auto_notes.map((note, i) => (
                            <li key={i}>{note}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {previewResult.calculation_details && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-muted-foreground">Ver detalles del cálculo</summary>
                        <pre className="mt-2 p-2 bg-background rounded overflow-auto">
                          {JSON.stringify(previewResult.calculation_details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="h-4 w-4 mr-2" />
            {t('actions.reset')}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('actions.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t('actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SchedulingRulesDialog;
